import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Injectable, inject } from '@angular/core';

import { AuthenticationService } from '../services/authentication.service';
import { UtilsService } from './utils';

@Injectable({
  providedIn: 'root',
})
export class PreloadStrategy implements PreloadingStrategy {
  private auth = inject(AuthenticationService);

  // Lista de rotas críticas que devem ser pré-carregadas com prioridade
  private criticalRoutes = ['user-dashboard', 'casino', 'casino-live', 'sports'];

  // Rotas que só devem ser pré-carregadas se o usuário estiver logado
  private authenticatedOnlyRoutes = ['user-dashboard', 'deposit', 'withdraw', 'profile'];

  constructor() {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!UtilsService.isBrowser()) return of(null);

    if (this.shouldPreloadRoute(route)) {
      return new Observable((observer) => {
        const callback = () => {
          load().subscribe({
            next: (data) => {
              observer.next(data);
              observer.complete();
            },
            error: (error) => observer.error(error),
          });
        };

        if (UtilsService.isBrowser()) {
          const isCriticalRoute = this.isCriticalRoute(route);
          const delay = isCriticalRoute ? 8000 : 12000;

          setTimeout(() => {
            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(callback, { timeout: 3000 });
            } else {
              callback();
            }
          }, delay);
        }
      });
    } else {
      return of(null);
    }
  }

  private shouldPreloadRoute(route: Route): boolean {
    // Verificar se a rota tem a propriedade preload explícita
    if (route.data && route?.data?.preload) {
      return true;
    }

    // Verificar se é uma rota crítica
    if (this.isCriticalRoute(route)) {
      return true;
    }

    // Verificar se é uma rota que requer autenticação
    if (this.isAuthenticatedOnlyRoute(route)) {
      // Só pré-carregar se o usuário estiver logado
      return this.isUserLoggedIn();
    }

    // Verificar rotas filhas
    if (route.loadChildren && route?.data?.preload === undefined && route.children) {
      return route.children.some((child) => this.shouldPreloadRoute(child));
    }

    return false;
  }

  private isCriticalRoute(route: Route): boolean {
    if (!route.path) return false;

    return this.criticalRoutes.some((criticalRoute) => route.path === criticalRoute || route.path?.includes(criticalRoute));
  }

  private isAuthenticatedOnlyRoute(route: Route): boolean {
    if (!route.path) return false;

    return this.authenticatedOnlyRoutes.some((authRoute) => route.path === authRoute || route.path?.includes(authRoute));
  }

  private isUserLoggedIn(): boolean {
    // Verificar se o usuário está logado através do AuthenticationService
    return this.auth.loggedInSubject.value || false;
  }
}
